const DAY_KEYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat']
const DAY_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

const WEEKDAY_SHORT_TO_INDEX = {
  Sun: 0,
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
}

/* -----------------------------
    Base class (shared parsing)
------------------------------ */

class StoreHoursBase extends HTMLElement {
  constructor() {
    super()
    this.timezone = ''
    this.regularHoursLines = []
    this.specialHoursLines = []

    this.schedule = {
      mon: [],
      tue: [],
      wed: [],
      thu: [],
      fri: [],
      sat: [],
      sun: [],
    }
    this.exceptions = []
  }

  _hydrateFromAttributes() {
    const tz = this.getAttribute('timezone')
    if (tz) this.timezone = tz

    const regularRaw = this.getAttribute('regular-hours')
    if (regularRaw) {
      try {
        const parsed = JSON.parse(regularRaw)
        this.regularHoursLines = Array.isArray(parsed) ? parsed : []
      } catch (_) {}
    }

    const specialRaw = this.getAttribute('special-hours')
    if (specialRaw) {
      try {
        const parsed = JSON.parse(specialRaw)
        this.specialHoursLines = Array.isArray(parsed) ? parsed : []
      } catch (_) {}
    }

    this._rebuildParsedData()
  }

  _rebuildParsedData() {
    this.schedule = this.parseRegularHoursLines(this.regularHoursLines)
    this.exceptions = this.parseSpecialHoursLines(this.specialHoursLines)
  }

  set data(v) {
    // preferred API
    // el.data = { timezone, regularHoursLines, specialHoursLines }
    if (!v || typeof v !== 'object') return

    if (typeof v.timezone === 'string') this.timezone = v.timezone
    if (Array.isArray(v.regularHoursLines))
      this.regularHoursLines = v.regularHoursLines
    if (Array.isArray(v.specialHoursLines))
      this.specialHoursLines = v.specialHoursLines

    this._rebuildParsedData()
    this.render()
  }

  /* -----------------------------
     Parsing helpers for line lists
  ------------------------------ */

  parseRegularHoursLines(lines) {
    const schedule = {
      mon: [],
      tue: [],
      wed: [],
      thu: [],
      fri: [],
      sat: [],
      sun: [],
    }

    if (!Array.isArray(lines)) return schedule

    for (const raw of lines) {
      if (!raw || typeof raw !== 'string') continue

      const parts = raw.split('|').map((s) => s.trim())
      if (parts.length < 2) continue

      const day = (parts[0] || '').toLowerCase()
      if (!schedule[day]) continue

      // day|closed
      if ((parts[1] || '').toLowerCase() === 'closed') {
        // explicit closed day, no ranges
        schedule[day] = []
        continue
      }

      // day|from|to
      if (parts.length >= 3) {
        const from = parts[1]
        const to = parts[2]
        if (this.isValidHHMM(from) && this.isValidHHMM(to)) {
          schedule[day].push([from, to])
        }
      }
    }

    // Sort each day's intervals by start time
    for (const key of Object.keys(schedule)) {
      schedule[key].sort((a, b) => this.parseHHMM(a[0]) - this.parseHHMM(b[0]))
    }

    return schedule
  }

  parseSpecialHoursLines(lines) {
    // Returns array of:
    // [{ date: "2026-12-25", closed: true, hours: [], label: "Christmas Day" }, ...]
    // grouped by date so multiple intervals can exist on same date
    const byDate = new Map()

    if (!Array.isArray(lines)) return []

    for (const raw of lines) {
      if (!raw || typeof raw !== 'string') continue

      const parts = raw.split('|').map((s) => s.trim())
      if (parts.length < 2) continue

      const date = parts[0]
      if (!this.isValidDateKey(date)) continue

      if (!byDate.has(date)) {
        byDate.set(date, { date, closed: false, hours: [], label: '' })
      }

      const row = byDate.get(date)
      const p1 = (parts[1] || '').toLowerCase()

      // YYYY-MM-DD|closed|optional label
      if (p1 === 'closed') {
        row.closed = true
        row.hours = []
        row.label = parts[2] || row.label || ''
        continue
      }

      // YYYY-MM-DD|from|to|optional label
      if (parts.length >= 3) {
        const from = parts[1]
        const to = parts[2]
        const label = parts[3] || ''

        if (this.isValidHHMM(from) && this.isValidHHMM(to)) {
          // if already marked closed, keep closed as highest priority
          if (!row.closed) {
            row.hours.push([from, to])
          }
          if (label && !row.label) row.label = label
        }
      }
    }

    const out = Array.from(byDate.values())

    // sort intervals inside each exception
    for (const ex of out) {
      ex.hours.sort((a, b) => this.parseHHMM(a[0]) - this.parseHHMM(b[0]))
    }

    // sort exceptions by date ascending
    out.sort((a, b) => a.date.localeCompare(b.date))

    return out
  }

  exceptionForDate(exceptions, dateKey) {
    if (!Array.isArray(exceptions)) return null
    return exceptions.find((x) => x && x.date === dateKey) || null
  }

  /* -----------------------------
     Time and timezone helpers
  ------------------------------ */

  isValidHHMM(s) {
    if (typeof s !== 'string') return false
    const m = s.match(/^(\d{2}):(\d{2})$/)
    if (!m) return false
    const hh = Number(m[1])
    const mm = Number(m[2])
    if (!Number.isInteger(hh) || !Number.isInteger(mm)) return false
    if (mm < 0 || mm > 59) return false
    if (hh < 0 || hh > 24) return false
    if (hh === 24 && mm !== 0) return false
    return true
  }

  isValidDateKey(s) {
    return typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s)
  }

  parseHHMM(s) {
    const [hStr, mStr] = String(s).split(':')
    const h = Number(hStr)
    const m = Number(mStr)
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null
    return h * 60 + m
  }

  format12h(hhmm) {
    const mins = this.parseHHMM(hhmm)
    if (mins == null) return ''

    let h = Math.floor(mins / 60)
    const m = mins % 60

    const ampm = h >= 12 ? 'PM' : 'AM'
    h = h % 12
    if (h === 0) h = 12

    return `${h}:${String(m).padStart(2, '0')} ${ampm}`
  }

  getZonedParts(timeZone, date = new Date()) {
    const fmt = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })

    const parts = fmt.formatToParts(date)
    const get = (type) => parts.find((p) => p.type === type)?.value

    const weekdayShort = get('weekday') // Mon, Tue...
    const year = get('year')
    const month = get('month')
    const day = get('day')
    const hour = get('hour')
    const minute = get('minute')

    const weekdayIndex = WEEKDAY_SHORT_TO_INDEX[weekdayShort]
    const dateKey = `${year}-${month}-${day}`
    const minutesNow = Number(hour) * 60 + Number(minute)

    return { weekdayIndex, dateKey, minutesNow }
  }

  getDateKeyOffset(timeZone, offsetDays) {
    const d = new Date(Date.now() + offsetDays * 24 * 60 * 60 * 1000)
    return this.getZonedParts(timeZone, d).dateKey
  }

  getTimeZoneLabel(timeZone) {
    try {
      const fmt = new Intl.DateTimeFormat('en-US', {
        timeZone,
        timeZoneName: 'short',
      })
      const parts = fmt.formatToParts(new Date())
      return parts.find((p) => p.type === 'timeZoneName')?.value || ''
    } catch (_) {
      return ''
    }
  }

  formatDateForUI(dateKey) {
    // dateKey is YYYY-MM-DD
    const formatted = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(dateKey))

    return formatted
  }

  /* -----------------------------
     Schedule resolution + status
  ------------------------------ */

  resolveDaySchedule(schedule, exceptions, dayKey, dateKey) {
    const base = Array.isArray(schedule?.[dayKey]) ? schedule[dayKey] : []
    const ex = this.exceptionForDate(exceptions, dateKey)

    if (!ex) return base
    if (ex.closed) return []
    if (Array.isArray(ex.hours) && ex.hours.length) return ex.hours

    return base
  }

  isInRange(nowMin, startMin, endMin) {
    // normal same-day range
    if (endMin > startMin) return nowMin >= startMin && nowMin < endMin

    // overnight range like 20:00 -> 02:00
    if (endMin < startMin) return nowMin >= startMin || nowMin < endMin

    // equal means no open window
    return false
  }

  computeOpenState({ schedule, exceptions, timeZone }) {
    const now = this.getZonedParts(timeZone)
    const todayKey = DAY_KEYS[now.weekdayIndex]
    const todayDateKey = now.dateKey

    const todayRanges = this.resolveDaySchedule(
      schedule,
      exceptions,
      todayKey,
      todayDateKey
    )
      .map(([a, b]) => [this.parseHHMM(a), this.parseHHMM(b), a, b])
      .filter(([a, b]) => a != null && b != null)

    // 1) Open in today's schedule
    for (const [startMin, endMin, , endStr] of todayRanges) {
      if (this.isInRange(now.minutesNow, startMin, endMin)) {
        return {
          isOpen: true,
          closesAt: endStr,
          closesDayIndex:
            endMin < startMin ? (now.weekdayIndex + 1) % 7 : now.weekdayIndex,
        }
      }
    }

    // 2) Open because yesterday had an overnight interval spilling into today
    const yIdx = (now.weekdayIndex + 6) % 7
    const yKey = DAY_KEYS[yIdx]
    const yDateKey = this.getDateKeyOffset(timeZone, -1)

    const yesterdayRanges = this.resolveDaySchedule(
      schedule,
      exceptions,
      yKey,
      yDateKey
    )
      .map(([a, b]) => [this.parseHHMM(a), this.parseHHMM(b), a, b])
      .filter(([a, b]) => a != null && b != null)

    for (const [startMin, endMin, , endStr] of yesterdayRanges) {
      if (endMin < startMin && now.minutesNow < endMin) {
        return {
          isOpen: true,
          closesAt: endStr,
          closesDayIndex: now.weekdayIndex,
        }
      }
    }

    // 3) Closed, find next opening
    // Later today
    for (const [startMin, endMin, startStr] of todayRanges) {
      if (endMin > startMin && now.minutesNow < startMin) {
        return {
          isOpen: false,
          opensAt: startStr,
          opensDayIndex: now.weekdayIndex,
        }
      }
    }

    // Next 7 days
    for (let offset = 1; offset <= 7; offset++) {
      const idx = (now.weekdayIndex + offset) % 7
      const dayKey = DAY_KEYS[idx]
      const dateKey = this.getDateKeyOffset(timeZone, offset)

      const ranges = this.resolveDaySchedule(
        schedule,
        exceptions,
        dayKey,
        dateKey
      )
        .map(([a, b]) => [this.parseHHMM(a), this.parseHHMM(b), a, b])
        .filter(([a, b]) => a != null && b != null)

      if (ranges.length) {
        return {
          isOpen: false,
          opensAt: ranges[0][2],
          opensDayIndex: idx,
        }
      }
    }

    return { isOpen: false, opensAt: null }
  }

  /* -----------------------------
     tiny HTML escaper
  ------------------------------ */

  escapeHtml(str) {
    return String(str)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;')
  }
}

/* -----------------------------
     Component 1: active status
  ------------------------------ */

class StoreHoursStatus extends StoreHoursBase {
  static get observedAttributes() {
    return [
      'timezone',
      'regular-hours',
      'special-hours',
      'tick-seconds',
      'show-tz-label',
    ]
  }

  constructor() {
    super()
    this._timer = null
    this._statusEl = null
  }

  connectedCallback() {
    this._statusEl = this.querySelector('[data-status]') || this
    this._hydrateFromAttributes()
    this.render()
    this._startTicker()
  }

  disconnectedCallback() {
    this._stopTicker()
  }

  attributeChangedCallback() {
    this._hydrateFromAttributes()
    this.render()
    this._startTicker()
  }

  get tickSeconds() {
    const n = Number(this.getAttribute('tick-seconds'))
    return Number.isFinite(n) && n > 0 ? n : 60
  }

  get showTzLabel() {
    const v = this.getAttribute('show-tz-label')
    return v === null ? true : v !== 'false'
  }

  _startTicker() {
    this._stopTicker()
    this._timer = setInterval(() => this.render(), this.tickSeconds * 1000)
  }

  _stopTicker() {
    if (this._timer) clearInterval(this._timer)
    this._timer = null
  }

  render() {
    if (!this._statusEl || !this.timezone) return

    const state = this.computeOpenState({
      schedule: this.schedule,
      exceptions: this.exceptions,
      timeZone: this.timezone,
    })

    const tzLabel = this.showTzLabel ? this.getTimeZoneLabel(this.timezone) : ''
    const tzSuffix = tzLabel ? ` ${tzLabel}` : ''

    let text = 'Closed'
    if (state.isOpen && state.closesAt) {
      text = `Open Now - Closes at ${this.format12h(state.closesAt)}${tzSuffix}`
    } else if (!state.isOpen && state.opensAt) {
      const daySuffix = Number.isFinite(state.opensDayIndex)
        ? ` - ${DAY_LABELS[state.opensDayIndex]}`
        : ''
      text = `Closed - Opens at ${this.format12h(state.opensAt)}${tzSuffix}${daySuffix}`
    } else if (tzSuffix) {
      text = `Closed${tzSuffix}`
    }

    this._statusEl.textContent = text
    this._statusEl.setAttribute('data-open', state.isOpen ? 'true' : 'false')
    this._statusEl.classList.toggle('is-open', !!state.isOpen)
    this._statusEl.classList.toggle('is-closed', !state.isOpen)

    this.dispatchEvent(
      new CustomEvent('hours-status-change', {
        bubbles: true,
        detail: { state, text },
      })
    )
  }
}

customElements.define('store-hours-status', StoreHoursStatus)

/* -----------------------------
     Component 2: weekly + special table
  ------------------------------ */

class StoreHoursTable extends StoreHoursBase {
  static get observedAttributes() {
    return ['timezone', 'regular-hours', 'special-hours', 'show-special-hours']
  }

  constructor() {
    super()
    this._weekEl = null
    this._specialEl = null
  }

  connectedCallback() {
    this._weekEl = this.querySelector('[data-week]')
    this._specialEl = this.querySelector('[data-special]')

    // Create targets if not provided
    if (!this._weekEl) {
      this._weekEl = document.createElement('div')
      this._weekEl.setAttribute('data-week', '')
      this.appendChild(this._weekEl)
    }

    if (!this._specialEl) {
      this._specialEl = document.createElement('div')
      this._specialEl.setAttribute('data-special', '')
      this.appendChild(this._specialEl)
    }

    this._hydrateFromAttributes()
    this.render()
  }

  attributeChangedCallback() {
    this._hydrateFromAttributes()
    this.render()
  }

  get showSpecialHours() {
    const v = this.getAttribute('show-special-hours')
    return v === null ? true : v !== 'false'
  }

  render() {
    if (!this.timezone) return
    this._renderWeek()
    this._renderSpecial()
  }

  _renderWeek() {
    const now = this.getZonedParts(this.timezone)
    const todayKey = DAY_KEYS[now.weekdayIndex]

    // Monday first
    const order = [1, 2, 3, 4, 5, 6, 0]

    const html = order
      .map((idx) => {
        const dayKey = DAY_KEYS[idx]
        const ranges = this.schedule[dayKey] || []
        const text = ranges.length
          ? ranges
              .map(([a, b]) => `${this.format12h(a)} - ${this.format12h(b)}`)
              .join(', ')
          : 'Closed'

        const isToday = dayKey === todayKey

        return `
          <div class="sh-row ${isToday ? 'is-today' : ''}" data-day="${dayKey}">
            <div class="sh-day">${DAY_LABELS[idx]}</div>
            <div class="sh-time">${text}</div>
          </div>
        `
      })
      .join('')

    this._weekEl.innerHTML = html
  }

  _renderSpecial() {
    if (!this._specialEl) return

    if (!this.showSpecialHours) {
      this._specialEl.innerHTML = ''
      return
    }

    if (!this.exceptions.length) {
      this._specialEl.innerHTML = ''
      return
    }

    const rows = this.exceptions
      .map((ex) => {
        let hoursText = 'Closed'
        if (!ex.closed && ex.hours.length) {
          hoursText = ex.hours
            .map(([a, b]) => `${this.format12h(a)} - ${this.format12h(b)}`)
            .join(', ')
        }

        const labelText = ex.label
          ? `<div class="sh-special-label">${this.escapeHtml(ex.label)}</div>`
          : ''

        return `
          <div class="sh-special-row" data-date="${ex.date}">
            ${labelText}
            <div class="sh-special-date">${this.formatDateForUI(ex.date)}</div>
            <div class="sh-special-hours">${hoursText}</div>
          </div>
        `
      })
      .join('')

    this._specialEl.innerHTML = `
        <div class="sh-special-title">${this.getAttribute('data-special-hours-label') || 'Special Hours'}</div>
        <div class="sh-special-list">${rows}</div>
      `
  }
}

customElements.define('store-hours-table', StoreHoursTable)
